<template>
    <Notification v-if="contentstream?.published_at === null" class="bg-red text-white border-b border-gray-300">
        <p>
            {{ $t('Content is not yet published and is displayed because you are logged in as administrator.') }}
        </p>
    </Notification>

    <Loading :loading="loading" class="min-h-app">
        <div v-if="fullscreen" ref="image_preview" id="draggableArea" class="bg-white fixed top-0 bottom-0 left-0 right-0 z-50 overflow-hidden">
            <div class="relative">
                <div @click="toggleFullscreen()" class="z-30 h-8 flex items-center text-gray-900 absolute top-[1rem] left-[1rem] cursor-pointer">
                    <span class="font-light">{{ $t('Back') }}</span>
                </div>
                <div
                    @click="toggleFullscreen()"
                    class="z-30 w-8 h-8 flex items-center justify-center rounded-full bg-[#CCCCCC] text-gray-900 absolute top-[1rem] right-[1rem] cursor-pointer"
                >
                    <Icon type="Scale" viewBox="0 0 19 18" width="12" height="12" class="fill-current" />
                </div>
                <div
                    @click="$refs.zoomer.zoomIn((scale = 1.5))"
                    v-if="fullscreen"
                    class="z-30 w-8 h-8 flex items-center justify-center rounded-full bg-[#CCCCCC] text-gray-900 absolute top-[1rem] right-[3.5rem] cursor-pointer"
                >
                    <Icon type="Search" viewBox="0 0 19 20" width="12" height="12" class="fill-current" />
                    <span class="font-light">+</span>
                </div>
                <div
                    @click="$refs.zoomer.zoomOut((scale = 0.5))"
                    v-if="fullscreen"
                    class="z-30 w-8 h-8 flex items-center justify-center rounded-full bg-[#CCCCCC] text-gray-900 absolute top-[1rem] right-[6rem] cursor-pointer"
                >
                    <Icon type="Search" viewBox="0 0 19 20" width="12" height="12" class="fill-current" />
                    <span class="font-light">-</span>
                </div>
                <v-zoomer ref="zoomer" :max-scale="10" :zooming-elastic="false" class="h-screen">
                    <img @contextmenu.prevent class="object-contain w-full h-full prevent-click" :src="fullscreenImage" />
                </v-zoomer>

                <!-- Image navigation for small screens -->
                <div v-show="fullscreen" class="absolute bottom-[1rem] left-0 right-0 z-50 lg:hidden">
                    <div class="bg-white rounded-md shadow-md w-fit max-w-full overflow-x-auto overflow-y-clip flex gap-2 p-2 mx-auto">
                        <span
                            @click="[slideToImage(index)]"
                            v-for="(item, index) in images"
                            :key="item.id"
                            :class="index === activeSliderIndex ? 'bg-blue text-white' : 'text-blue'"
                            class="border border-blue p-2 rounded-md shadow-md cursor-pointer whitespace-nowrap"
                        >
                            {{ $translate(item.attributes.title) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div class="mx-auto container min-h-app flex flex-col justify-between" v-if="!loading">
            <div class="grid grid-cols-1 gap-8 relative lg:grid-cols-2 min-h-screen">
                <div id="content" class="py-4 shrink-0 col-span-1" :class="[fullscreen ? 'lg:absolute z-50 top-12 hidden lg:block' : 'top-[unset] left-[unset]']">
                    <div :class="fullscreen ? 'overflow-y-auto w-[400px] h-[700px] bg-white p-4 rounded-md shadow-md' : ''">
                        <div class="flex flex-row justify-between items-center" id="contentheader">
                            <div
                                v-if="fullscreen"
                                @click="fullscreen = !fullscreen"
                                class="flex flex-row gap-2 group-hover:gap-3 transition-all ease-in-out duration-500 items-center cursor-pointer"
                            >
                                <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="10" class="fill-none stroke-current transform rotate-180" />
                                <span>
                                    {{ $t('Back') }}
                                </span>
                            </div>

                            <!-- Overview (Back link) -->
                            <router-link v-else :to="link" class="flex flex-row gap-2 group-hover:gap-3 transition-all ease-in-out duration-500 items-center cursor-pointer">
                                <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="10" class="fill-none stroke-current transform rotate-180" />
                                <span>
                                    {{ $t('Overview') }}
                                </span>
                            </router-link>

                            <!-- Published date -->
                            <Icon v-if="fullscreen" type="Move" viewBox="0 0 60 60" width="20" height="20" class="mr-0 fill-current" />
                            <span>
                                {{ formatDateByRegion($moment(contentstream.published_at)) }}
                            </span>
                        </div>

                        <hr class="my-4 border-gray-300" />

                        <!-- Corresponding product with link -->
                        <div v-if="contentstream?.product">
                            <router-link :to="`/dashboard/videoanalysis?selected_product_ids=${contentstream.product.id}`" class="flex flex-col lg:flex-row gap-4 lg:items-center">
                                <div v-if="contentstream.product" class="text-white w-[50px] h-[50px] flex-shrink-0">
                                    <img
                                        :alt="$translate(contentstream.product.name)"
                                        class="w-full h-full object-contain prevent-click"
                                        :src="contentstream.product.image.replace('.png', '-gradient.png')"
                                    />
                                </div>
                                <div>
                                    <strong v-if="contentstream.product">
                                        {{ $translate(contentstream.product.name) }}
                                    </strong>
                                    <br />
                                    <small>
                                        {{ $translate(contentstream.title) }}
                                    </small>
                                </div>
                            </router-link>
                        </div>

                        <hr class="my-4 border-gray-300" />

                        <!-- With Tabs -->
                        <div v-if="tabContent('tab').length" ref="content">
                            <Tabs :data="tabContent('tab')" :activeTab="0" activeColor="#353CEE" :firstOnLoad="true" v-if="tabContent('tab').length > 1">
                                <template #header="{ data }">
                                    <strong class="whitespace-nowrap">
                                        {{ $translate(data.attributes?.title) ? $translate(data.attributes?.title) : 'Video-Analyse' }}
                                    </strong>
                                </template>

                                <template #item="{ key, data }">
                                    <div v-if="data.attributes?.wysiwyg">
                                        <div class="content fr-view" v-html="$translate(data.attributes.wysiwyg)"></div>
                                    </div>
                                    <div v-if="data?.attributes?.bunny">
                                        <div style="position: relative; padding-top: 56.25%">
                                            <iframe
                                                :src="data?.attributes?.bunny"
                                                loading="lazy"
                                                style="border: 0; position: absolute; top: 0; height: 100%; width: 100%"
                                                allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                                                allowfullscreen="true"
                                            ></iframe>
                                        </div>
                                    </div>
                                    <div v-else-if="data.attributes?.video" class="relative">
                                        <VideoPlayer :ref="'video_player_' + key" :source="data.attributes.video" :options="videoOptions">
                                            <div class="z-10 preview absolute top-0 bottom-0 left-0 right-0 bg-white rounded-md">
                                                <div
                                                    @click="$refs['video_player_' + key].togglePlay()"
                                                    class="h-full w-full text-white p-4 rounded-md flex flex-col justify-between bg-cover bg-no-repeat bg-center"
                                                    :style="'background-image: url(' + $asset('/hkcm/images/video-cover.png', 'public') + ')'"
                                                >
                                                    <div>
                                                        <p class="text-xxs">
                                                            {{ formatDateByRegion($moment(contentstream.published_at)) }}
                                                        </p>
                                                    </div>
                                                    <div class="flex flex-col lg:flex-row gap-4 lg:items-center">
                                                        <div v-if="contentstream?.product" class="text-white w-[50px] h-[50px] rounded-md flex-shrink-0">
                                                            <img
                                                                v-if="contentstream.product.image"
                                                                :alt="$translate(contentstream.contentstreamType.title)"
                                                                class="w-full h-full object-contain"
                                                                ref="svg"
                                                                :src="contentstream.product.image.replace('.png', '-gradient.png')"
                                                            />
                                                        </div>
                                                        <div>
                                                            <p class="text-xxs">
                                                                {{ $translate(contentstream.contentstreamType.title) }}
                                                            </p>
                                                            <strong v-if="contentstream?.product">
                                                                {{ $translate(contentstream.product.name) }}
                                                            </strong>
                                                        </div>
                                                    </div>
                                                    <button type="button" class="plyr__control plyr__control--overlaid !block" data-plyr="play" aria-label="Play">
                                                        <svg aria-hidden="true" focusable="false">
                                                            <use xlink:href="#plyr-play"></use>
                                                        </svg>
                                                        <span class="plyr__sr-only">Play</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </VideoPlayer>
                                    </div>
                                </template>
                            </Tabs>

                            <!-- WITHOUT Tabs -->
                            <div v-else>
                                <div v-if="tabContent('tab')[0].attributes?.wysiwyg">
                                    <div class="content fr-view" v-html="$translate(tabContent('tab')[0].attributes.wysiwyg)"></div>
                                </div>
                                <div v-if="tabContent('tab')[0].attributes?.bunny">
                                    <div style="position: relative; padding-top: 56.25%">
                                        <iframe
                                            :src="tabContent('tab')[0].attributes?.bunny"
                                            loading="lazy"
                                            style="border: 0; position: absolute; top: 0; height: 100%; width: 100%"
                                            allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                                            allowfullscreen="true"
                                        ></iframe>
                                    </div>
                                </div>
                                <div v-else-if="tabContent('tab')[0].attributes?.video" class="relative">
                                    <VideoPlayer ref="video_player" :source="tabContent('tab')[0].attributes.video" :options="videoOptions">
                                        <div class="z-10 preview absolute top-0 bottom-0 left-0 right-0 bg-white rounded-md">
                                            <div
                                                @click="$refs['video_player'].togglePlay()"
                                                class="h-full w-full text-white p-4 rounded-md flex flex-col justify-between bg-cover bg-no-repeat bg-center"
                                                :style="'background-image: url(' + $asset('/hkcm/images/video-cover.png', 'public') + ')'"
                                            >
                                                <div>
                                                    <p class="text-xxs">
                                                        {{ formatDateByRegion($moment(contentstream.published_at)) }}
                                                    </p>
                                                </div>
                                                <div class="flex flex-col lg:flex-row gap-4 lg:items-center">
                                                    <div v-if="contentstream?.product" class="text-white w-[50px] h-[50px] rounded-md flex-shrink-0">
                                                        <img
                                                            :alt="$translate(contentstream.contentstreamType.title)"
                                                            class="w-full h-full object-contain"
                                                            ref="svg"
                                                            :src="contentstream.product.image.replace('.png', '-gradient.png')"
                                                        />
                                                    </div>
                                                    <div>
                                                        <p class="text-xxs">
                                                            {{ $translate(contentstream.contentstreamType.title) }}
                                                        </p>
                                                        <strong v-if="contentstream?.product">
                                                            {{ $translate(contentstream.product.name) }}
                                                        </strong>
                                                    </div>
                                                </div>
                                                <button type="button" class="plyr__control plyr__control--overlaid !block" data-plyr="play" aria-label="Play">
                                                    <svg aria-hidden="true" focusable="false">
                                                        <use xlink:href="#plyr-play"></use>
                                                    </svg>
                                                    <span class="plyr__sr-only">Play</span>
                                                </button>
                                            </div>
                                        </div>
                                    </VideoPlayer>
                                </div>
                            </div>
                        </div>

                        <div v-show="fullscreen" class="absolute -bottom-14 left-0 right-0 z-10 p-8">
                            <div class="bg-white rounded-md shadow-md w-fit max-w-full overflow-x-auto overflow-y-clip flex gap-2 p-2 mx-auto">
                                <span
                                    @click="[slideToImage(index)]"
                                    v-for="(item, index) in images"
                                    :key="item.id"
                                    :class="index === activeSliderIndex ? 'bg-blue text-white' : 'text-blue'"
                                    class="border border-blue p-2 rounded-md shadow-md cursor-pointer whitespace-nowrap"
                                >
                                    {{ $translate(item.attributes.title) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div :class="type === 'post' ? 'lg:col-span-1' : 'lg:col-span-1'" class="py-4 shrink-0">
                    <div class="relative w-full h-full">
                        <div class="sticky w-[inherit]" style="top: var(--headerHeight)">
                            <div v-if="images.length" class="bg-white shadow-md rounded-md p-4 relative">
                                <!-- Image Slider -->
                                <div class="image-slider overflow-x-hidden">
                                    <div class="swiper-wrapper relative">
                                        <div v-for="item in images" :key="item.id" class="swiper-slide">
                                            <div
                                                @click="toggleFullscreen(item.attributes.image)"
                                                class="z-30 w-8 h-8 flex items-center justify-center rounded-full bg-[#CCCCCC] text-gray-900 absolute top-4 right-4 cursor-pointer"
                                            >
                                                <Icon type="Scale" viewBox="0 0 19 18" width="12" height="12" class="fill-current" />
                                            </div>
                                            <img class="prevent-click" @click="toggleFullscreen(item.attributes.image)" :src="item.attributes.image" alt="slider-image" />
                                        </div>
                                    </div>
                                </div>

                                <!-- Image Title Click -->
                                <div class="absolute -bottom-14 left-0 right-0 z-10 p-8">
                                    <div class="bg-white rounded-md shadow-md w-fit max-w-full overflow-x-auto overflow-y-clip flex gap-2 p-2 mx-auto">
                                        <span
                                            @click="[slideToImage(index)]"
                                            v-for="(item, index) in images"
                                            :key="item.id"
                                            :class="index === activeSliderIndex ? 'bg-blue text-white' : 'text-blue'"
                                            class="border border-blue p-2 rounded-md shadow-md cursor-pointer whitespace-nowrap"
                                        >
                                            {{ $translate(item.attributes.title) }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <hr class="mt-20 w-full border-b-0.5 border-gray-300" />
                            <!-- Past Analysis (right bar) -->
                            <div v-if="contentstream?.product">
                                <PastAnalysis
                                    class="mt-4"
                                    :product_id="contentstream.product.id"
                                    :contentstream_id="contentstream.id"
                                    :contentstream_type_id="contentstream.contentstream_type_id"
                                    :page="this.$route.params.page"
                                    :query_params="query_params"
                                    :active_tab="this.$route.params.active_tab"
                                    :type="this.type"
                                    origin="videoanalysis"
                                />
                                <!-- Past Analysis Link -->
                                <router-link
                                    :to="`/dashboard/videoanalysis?selected_product_ids=${contentstream.product.id}`"
                                    class="flex flex-row gap-2 group-hover:gap-3 transition-all ease-in-out duration-500 items-center cursor-pointer mt-6"
                                >
                                    <span>
                                        {{ $t('All Past Analysis') }}
                                    </span>
                                    <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="10" class="fill-none stroke-current transform" />
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <hr class="mt-8 w-full border-b-0.5 border-gray-300" />
            <Disclaimer />
        </div>
    </Loading>
</template>
<script>
import Swiper, { Navigation } from 'swiper';

Swiper.use([Navigation]);

import { mapGetters } from 'vuex';
import { getActiveLanguage, trans } from 'laravel-vue-i18n';
import { formatDateByRegion } from '../../helpers.js';

export default {
    props: ['type', 'page', 'active_tab'],

    data: () => ({
        loading: true,
        backgroundColor: null,
        backgroundGradient: null,
        videoOptions: {
            storage: {
                enabled: true,
                key: 'plyr'
            }
        },
        slider: null,
        activeSliderIndex: 0,
        fullscreen: false,
        fullscreenImage: null,
        getActiveLanguage: getActiveLanguage(),
        lastScrollPosition: null,
        images: []
    }),

    mounted() {
        this.id = this.$route.params.id;
        this.loading = true;
        this.init(this.id);
    },

    methods: {
        formatDateByRegion,
        async init(id) {
            this.images = [];

            await this.$store.dispatch('contentstream/show', id).then((response) => {
                this.collectImages();

                this.loading = false;

                this.$nextTick(() => {
                    this.initSlider();
                });
            });
        },

        collectImages() {
            this.contentstream.content.forEach((item) => {
                if (item?.layout === 'image' && item) {
                    this.images.push(item);
                } else if (item?.layout === 'tab_wysiwyg_with_images' && item?.attributes?.images?.length > 0) {
                    this.images = [...this.images, ...item.attributes.images];
                }
            });
        },

        toggleFullscreen(image) {
            this.fullscreen = !this.fullscreen;

            if (this.fullscreen) {
                this.lastScrollPosition = window.scrollY;

                // making the content div draggable with the dragElement helper
                this.$dragElement(document.getElementById('content'));

                this.$store.commit('settings/closeBackendSidebar', { name: 'backend_sidebar' });
            } else {
                this.$nextTick(() => {
                    window.scrollTo({
                        top: this.lastScrollPosition
                    });
                });

                this.$store.commit('settings/openBackendSidebar', { name: 'backend_sidebar' });
            }

            this.fullscreen ? (this.fullscreenImage = image) : (this.fullscreenImage = null);
        },

        tabContent(layout) {
            return this.contentstream.content.filter((item) => {
                // Found ISIN and make it clickable
                if (item.layout === 'tab_wysiwyg') {
                    let regex = new RegExp('ISIN: ?([0-9a-z]{12})', 'ig');
                    let founds;

                    do {
                        founds = regex.exec(this.$translate(item.attributes.wysiwyg));
                        if (founds) {
                            let index = this.contentstream.content.indexOf(item);
                            this.contentstream.content[index].attributes.wysiwyg[this.getActiveLanguage] = this.contentstream.content[index].attributes.wysiwyg[
                                this.getActiveLanguage
                            ].replace(founds[0], '<span class="copy-on-click cursor-pointer"><span>ISIN:</span>' + founds[1] + '</span>');
                        }
                    } while (founds);
                }

                if (layout === 'tab') {
                    return item.layout === 'video' || item.layout === 'tab_wysiwyg' || item.layout === 'tab_wysiwyg_with_images';
                } else if (layout === 'image' && item?.attributes?.images?.length > 0) {
                    return true;
                } else {
                    return item.layout === layout;
                }
            });
        },

        slideToImage(index) {
            this.slider.slideTo(index);
            this.activeSliderIndex = index;
            this.fullscreenImage = this.images[index].attributes.image;
        },

        initSlider() {
            this.slider = new Swiper('.image-slider', {
                slidesPerView: 1,
                imageSize: 'cover'
            });
        }
    },

    computed: {
        ...mapGetters('contentstream', {
            contentstream: 'get'
        }),

        background() {
            this.backgroundColor = this.contentstream?.product?.productGroups?.data[0]?.productCategories?.data[0]?.background_color;
            this.backgroundGradient = this.contentstream?.product?.productGroups?.data[0]?.productCategories?.data[0]?.background_gradient;

            return 'linear-gradient(to bottom, ' + this.backgroundColor + ' 0%, ' + this.backgroundGradient + ' 100%)';
        },

        link() {
            return `/dashboard/videoanalysis?${this.query_params?.toString()}`;
        },

        query_params() {
            let params = {};

            this.$route.query.active_tab ? (params['active_tab'] = this.$route.query.active_tab) : (params['active_tab'] = 'all_analysis');

            this.$route.query.published_at ? (params['published_at'] = this.$route.query.published_at) : '';

            this.$route.query.selected_product_ids?.length > 0 ? (params['selected_product_ids'] = this.$route.query.selected_product_ids) : '';

            this.$route.query.page ? (params['page'] = this.$route.query.page) : (params['page'] = 1);

            params['type'] = 'videoanalysis';

            let combinedParams = new URLSearchParams(params);

            return combinedParams;
        }
    },

    watch: {
        '$route.fullPath': async function () {
            if (this.$route.params.id) {
                this.loading = true;
                await this.init(this.$route.params.id);
                this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
.chocolat-image-wrapper {
    pointer-events: none;
}
</style>
